/* Leave requests: teacher (own, with MC/Emergency) and parents (for a child).
   - Standard student leave needs 7 days' notice (late -> admin exception)
   - MC needs medical evidence; EL needs a reason (+evidence where applicable)
   - Student leave counts toward the 7-per-cycle student quota; teacher leave never does
   - Admin previews ALL affected classes BEFORE approving teacher leave
   - Approving teacher leave auto-creates/links replacement (reschedule) requests */
import { useEffect, useRef, useState } from "react";
import api, { useList, errMsg } from "@/lib/api";
import { PageHeader, StatusBadge, Spinner, EmptyState } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Calendar } from "@/components/ui/calendar";
import { toast } from "sonner";
import { Plane, X, Check, CalendarOff, Paperclip, Eye, Users, User, Gauge, BellRing, RefreshCcw } from "lucide-react";

const leaveTimes = (l) =>
  l.start_time ? `${l.start_time}–${l.end_time || "end of day"}` : "Full day";

const CATEGORY_LABELS = { standard: "Standard leave", mc: "Medical (MC)", emergency: "Emergency leave" };

export function CategoryBadge({ category }) {
  const styles = {
    standard: "bg-muted text-foreground",
    mc: "bg-[#FFF3E0] text-[#B45309]",
    emergency: "bg-[#FFEDEC] text-destructive",
  };
  return (
    <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${styles[category] || styles.standard}`}
      data-testid={`category-badge-${category}`}>
      {CATEGORY_LABELS[category] || "Standard leave"}
    </span>
  );
}

async function uploadEvidence(file) {
  const fd = new FormData();
  fd.append("file", file);
  const { data } = await api.post("/leave-requests/upload-evidence", fd,
    { headers: { "Content-Type": "multipart/form-data" } });
  return data;
}

// ------------------------------------------------------------------ request dialog
function LeaveRequestDialog({ role, open, onOpenChange, onCreated }) {
  const { data: children } = useList(role === "parent" && open ? "/parent/children" : null, [open]);
  const [studentId, setStudentId] = useState("");
  const [category, setCategory] = useState("standard");
  const [date, setDate] = useState("");
  const [fullDay, setFullDay] = useState(true);
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [reason, setReason] = useState("");
  const [suggestion, setSuggestion] = useState("");
  const [quota, setQuota] = useState(null);
  const [lessonDates, setLessonDates] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  // P2: structured proposal
  const [proposalMode, setProposalMode] = useState("none");
  const [specificStudentId, setSpecificStudentId] = useState("");
  const [specificDate, setSpecificDate] = useState("");
  const [specificStart, setSpecificStart] = useState("");
  const [specificEnd, setSpecificEnd] = useState("");
  const [openSlotsList, setOpenSlotsList] = useState([]);
  const [slotDate, setSlotDate] = useState("");
  const [slotStart, setSlotStart] = useState("");
  const [slotEnd, setSlotEnd] = useState("");
  const [replacementDay, setReplacementDay] = useState("");
  const [holidayDate, setHolidayDate] = useState("");
  const [holidays, setHolidays] = useState(null);
  // Parent-picked replacement slot (auto-approve path)
  const [parentReplSlot, setParentReplSlot] = useState(null);   // { class_schedule_id, date, start_time, end_time }
  const [deferToTeacher, setDeferToTeacher] = useState(false);
  const [teacherGaps, setTeacherGaps] = useState(null);         // { days_with_slots: [] }
  const [gapsLoading, setGapsLoading] = useState(false);

  useEffect(() => {
    if (role === "parent" && studentId) {
      api.get("/reschedule/quota", { params: { student_id: studentId } })
        .then((r) => setQuota(r.data)).catch(() => setQuota(null));
      api.get("/parent/lesson-dates", { params: { student_id: studentId, weeks: 5 } })
        .then((r) => setLessonDates(r.data)).catch(() => setLessonDates([]));
      setDate("");
    }
  }, [role, studentId]);

  useEffect(() => {
    if (role === "teacher" && open) {
      api.get("/teacher/teaching-dates", { params: { weeks: 12 } })
        .then((r) => setLessonDates(r.data)).catch(() => setLessonDates([]));
    }
  }, [role, open]);

  // P5: fetch observed Yamaha/Public holidays when the holiday_mirror mode is picked
  useEffect(() => {
    if (proposalMode !== "holiday_mirror" || role !== "teacher") return;
    if (holidays) return;
    const year = new Date().getFullYear();
    Promise.all([
      api.get("/teacher/school-holidays", { params: { year } }).catch(() => ({ data: [] })),
      api.get("/teacher/school-holidays", { params: { year: year + 1 } }).catch(() => ({ data: [] })),
    ]).then(([a, b]) => {
      const all = [...(a.data || []), ...(b.data || [])];
      // Only observed Yamaha or Public holidays are eligible (§18)
      setHolidays(all.filter((h) =>
        h.is_observed && ["yamaha_school_holiday", "public_holiday"].includes(h.holiday_type)));
    });
  }, [proposalMode, role, holidays]);

  // Parent-initiated leave: fetch teacher gaps for the picked lesson
  useEffect(() => {
    setParentReplSlot(null);
    setTeacherGaps(null);
    if (role !== "parent" || category !== "standard" || !date || !lessonDates) return;
    const picked = lessonDates.find((d) => d.date === date);
    if (!picked || !picked.lessons?.length) return;
    // Support one lesson per day for the auto-approve flow; if the day has
    // multiple lessons the parent can still submit but without the auto-flow.
    if (picked.lessons.length !== 1) return;
    const schedId = picked.lessons[0].class_schedule_id;
    if (!schedId) return;
    setGapsLoading(true);
    api.get("/reschedule/teacher-gaps", { params: { schedule_id: schedId, days: 60 } })
      .then((r) => setTeacherGaps({ ...r.data, class_schedule_id: schedId }))
      .catch(() => setTeacherGaps({ days_with_slots: [], class_schedule_id: schedId }))
      .finally(() => setGapsLoading(false));
  }, [role, category, date, lessonDates]);


  const submit = async () => {
    if (!date || !reason || (role === "parent" && !studentId)) {
      toast.error("Please complete all fields");
      return;
    }
    if (!fullDay && (!startTime || !endTime)) {
      toast.error("Enter the leave start and end time, or choose full day");
      return;
    }
    if (role === "parent" && category === "standard"
        && teacherGaps && (teacherGaps.days_with_slots || []).length > 0
        && !parentReplSlot && !deferToTeacher) {
      toast.error("Pick a replacement slot or tick \"Ask teacher to propose\" to auto-approve");
      return;
    }
    setBusy(true);
    try {
      let evidence = { evidence_url: null, evidence_filename: null };
      const file = fileRef.current?.files?.[0];
      if (file) evidence = await uploadEvidence(file);
      // P2: build structured proposal payload
      let proposalMode2 = null;
      let proposalData = null;
      if (role === "teacher" && proposalMode !== "none") {
        proposalMode2 = proposalMode;
        if (proposalMode === "specific") {
          if (!specificStudentId || !specificDate || !specificStart || !specificEnd) {
            toast.error("Specific proposal needs student + date + times"); setBusy(false); return;
          }
          proposalData = { student_id: specificStudentId, replacement_date: specificDate,
                           start_time: specificStart, end_time: specificEnd };
        } else if (proposalMode === "open_slots" || proposalMode === "fcfs_open_slots") {
          if (openSlotsList.length === 0) {
            toast.error("Add at least one slot"); setBusy(false); return;
          }
          proposalData = { slots: openSlotsList, fcfs: proposalMode === "fcfs_open_slots" };
        } else if (proposalMode === "replacement_day") {
          if (!replacementDay) { toast.error("Pick a replacement day"); setBusy(false); return; }
          proposalData = { replacement_day: replacementDay, mirror_original_times: true };
        } else if (proposalMode === "holiday_mirror") {
          if (!holidayDate) { toast.error("Pick a holiday date"); setBusy(false); return; }
          proposalData = { holiday_date: holidayDate };
        }
      }
      const { data } = await api.post("/leave-requests", {
        leave_date: date, reason, leave_category: category,
        start_time: fullDay ? null : startTime, end_time: fullDay ? null : endTime,
        student_id: role === "parent" ? studentId : null,
        replacement_suggestion: role === "teacher" ? suggestion || null : null,
        proposal_mode: proposalMode2, proposal_data: proposalData,
        parent_replacement_slot: (role === "parent" && parentReplSlot) ? parentReplSlot : null,
        defer_to_teacher: role === "parent" && deferToTeacher,
        class_schedule_id: (role === "parent" && !parentReplSlot && deferToTeacher && teacherGaps)
                            ? teacherGaps.class_schedule_id : null,
        ...evidence,
      });
      if (data.status === "pending_evidence") {
        toast.warning("Submitted — evidence is still required before the school can approve.");
      } else if (data.status === "admin_exception") {
        toast.warning("Submitted as an admin exception (less than 7 days' notice).");
      } else if (data.status === "quota_exceeded") {
        toast.warning("Submitted, but the student's request quota is used up — admin will review.");
      } else if (role === "parent" && deferToTeacher) {
        toast.success("Leave auto-approved — the teacher will propose a replacement time.");
      } else if (role === "parent" && parentReplSlot) {
        toast.success("Leave auto-approved — your replacement slot is now with the teacher for confirmation.");
      } else {
        toast.success("Leave request submitted — the school will review it");
      }
      onOpenChange(false);
      setStudentId(""); setDate(""); setFullDay(true); setStartTime(""); setEndTime("");
      setReason(""); setCategory("standard"); setSuggestion(""); setQuota(null);
      setProposalMode("none"); setSpecificStudentId(""); setSpecificDate("");
      setSpecificStart(""); setSpecificEnd(""); setOpenSlotsList([]);
      setSlotDate(""); setSlotStart(""); setSlotEnd("");
      setReplacementDay(""); setHolidayDate("");
      setParentReplSlot(null); setTeacherGaps(null);
      if (fileRef.current) fileRef.current.value = "";
      onCreated();
    } catch (e) { toast.error(errMsg(e), { duration: 7000 }); } finally { setBusy(false); }
  };

  const needsEvidence = category === "mc" || category === "emergency";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="leave-request-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Request leave</DialogTitle>
          <DialogDescription>
            {role === "parent"
              ? "Standard leave needs 7 days' notice — you'll pick a replacement slot from the teacher's 60-day availability (or ask the teacher to propose) and the leave auto-approves. MC/Emergency can be same-day but need evidence/reason."
              : "Your approved leave blocks bookings; affected families are notified and replacement requests are created."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {role === "parent" && (
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Child</Label>
              <Select value={studentId} onValueChange={setStudentId}>
                <SelectTrigger data-testid="leave-child-select"><SelectValue placeholder="Select child..." /></SelectTrigger>
                <SelectContent>
                  {(children || []).map((c) => <SelectItem key={c.id} value={c.id}>{c.full_name}</SelectItem>)}
                </SelectContent>
              </Select>
              {quota && (
                <p className={`text-xs flex items-center gap-1 ${quota.exceeded ? "text-destructive font-semibold" : "text-muted-foreground"}`}
                  data-testid="leave-quota-hint">
                  <Gauge className="h-3 w-3" />
                  {quota.used}/{quota.limit} requests used this cycle ({quota.cycle_start} → {quota.cycle_end})
                </p>
              )}
            </div>
          )}
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Type</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger data-testid="leave-category-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="standard">Standard leave (7 days' notice)</SelectItem>
                <SelectItem value="mc">Medical — MC (evidence required)</SelectItem>
                <SelectItem value="emergency">Emergency leave</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Leave date</Label>
            {role === "parent" ? (
              !studentId ? (
                <p className="text-xs text-muted-foreground border border-dashed rounded-md p-2.5">Select a child first — leave applies to their actual lesson dates.</p>
              ) : !lessonDates ? <Spinner /> : lessonDates.length === 0 ? (
                <p className="text-xs text-muted-foreground border border-dashed rounded-md p-2.5">No upcoming lessons found for this child.</p>
              ) : (
                <Select value={date} onValueChange={setDate}>
                  <SelectTrigger data-testid="leave-date-select"><SelectValue placeholder="Select a lesson date..." /></SelectTrigger>
                  <SelectContent>
                    {lessonDates.map((d) => (
                      <SelectItem key={d.date} value={d.date}>
                        {d.day_name} {new Date(d.date + "T00:00:00").toLocaleDateString(undefined, { day: "numeric", month: "short" })} — {d.lessons.map((l) => `${l.course_name} ${l.start_time}`).join(" · ")}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )
            ) : (
              !lessonDates ? <Spinner /> : lessonDates.length === 0 ? (
                <p className="text-xs text-muted-foreground border border-dashed rounded-md p-2.5">No upcoming teaching days found in the next 12 weeks.</p>
              ) : (() => {
                const teachingSet = new Set(lessonDates.map((d) => d.date));
                const picked = lessonDates.find((d) => d.date === date);
                const parseISO = (s) => { const [y, m, dd] = s.split("-").map(Number); return new Date(y, m - 1, dd); };
                const toISO = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
                const firstDate = parseISO(lessonDates[0].date);
                const lastDate = parseISO(lessonDates[lessonDates.length - 1].date);
                return (
                  <div className="space-y-2" data-testid="teacher-leave-calendar-wrap">
                    <div className="rounded-md border bg-card">
                      <Calendar
                        mode="single"
                        selected={date ? parseISO(date) : undefined}
                        onSelect={(d) => d && setDate(toISO(d))}
                        disabled={(d) => !teachingSet.has(toISO(d))}
                        defaultMonth={date ? parseISO(date) : firstDate}
                        fromDate={firstDate}
                        toDate={lastDate}
                        modifiers={{ teaching: (d) => teachingSet.has(toISO(d)) }}
                        modifiersClassNames={{
                          teaching: "bg-primary/10 text-foreground font-semibold hover:bg-primary/20",
                        }}
                        data-testid="teacher-leave-calendar"
                      />
                    </div>
                    <p className="text-[10px] text-muted-foreground">
                      Only your teaching days (highlighted) can be selected. Non-teaching days are disabled.
                    </p>
                    {picked && (
                      <div className="rounded-md border border-primary/30 bg-accent/40 p-2.5 space-y-1.5"
                        data-testid="affected-classes-preview">
                        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                          Affected classes on {picked.day_name} {new Date(picked.date + "T00:00:00").toLocaleDateString(undefined, { day: "numeric", month: "short" })}
                        </p>
                        <ul className="space-y-1">
                          {picked.lessons.map((l, i) => (
                            <li key={i} className="text-xs flex items-center justify-between gap-2"
                              data-testid={`affected-lesson-${i}`}>
                              <span className="font-mono font-semibold">{l.start_time}–{l.end_time}</span>
                              <span className="flex-1 truncate">
                                {l.course_name}{l.student_name ? ` · ${l.student_name}` : ""}
                              </span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                );
              })()
            )}
          </div>
          <div className="flex items-center justify-between rounded-md border p-3">
            <Label className="text-sm">Full day</Label>
            <Switch checked={fullDay} onCheckedChange={setFullDay} data-testid="leave-fullday-switch" />
          </div>
          {!fullDay && (
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">From</Label>
                <Input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} data-testid="leave-start-time" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">To</Label>
                <Input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} data-testid="leave-end-time" />
              </div>
            </div>
          )}
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Reason</Label>
            <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="leave-reason-input" />
          </div>
          {role === "parent" && category === "standard" && date && (
            <div className="rounded-md border border-primary/30 bg-accent/40 p-3 space-y-2"
              data-testid="parent-replacement-panel">
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Pick your replacement slot
                </Label>
                <p className="text-[11px] text-muted-foreground mt-0.5">
                  Choose a free time in the teacher&apos;s 60-day availability (no existing class, no
                  replacement, no trial, no leave). Your leave will auto-approve and the teacher
                  will confirm the studio (or counter-propose another time).
                </p>
              </div>
              {gapsLoading ? <Spinner /> :
               !teacherGaps ? (
                 <p className="text-xs text-muted-foreground">Select a lesson date to see the teacher&apos;s free slots.</p>
               ) : (teacherGaps.days_with_slots || []).length === 0 ? (
                 <p className="text-xs text-destructive">
                   No open slots in the next 60 days. Use &quot;Ask teacher to propose&quot; below.
                 </p>
               ) : (
                 <div className="max-h-64 overflow-y-auto space-y-2" data-testid="teacher-gaps-list">
                   {teacherGaps.days_with_slots.map((d) => (
                     <div key={d.date} className="space-y-1">
                       <p className="text-[11px] font-semibold text-muted-foreground">
                         {d.day_name} · {new Date(d.date + "T00:00:00").toLocaleDateString(undefined, { day: "numeric", month: "short" })}
                       </p>
                       <div className="flex flex-wrap gap-1.5">
                         {d.slots.map((s, i) => {
                           const selected = parentReplSlot &&
                             parentReplSlot.date === d.date &&
                             parentReplSlot.start_time === s.start_time;
                           return (
                             <button
                               key={i}
                               type="button"
                               onClick={() => { setDeferToTeacher(false); setParentReplSlot({
                                 class_schedule_id: teacherGaps.class_schedule_id,
                                 date: d.date, start_time: s.start_time, end_time: s.end_time,
                               }); }}
                               className={`text-xs font-mono rounded-full px-2.5 py-1 border transition ${
                                 selected
                                   ? "bg-primary text-primary-foreground border-primary"
                                   : "bg-card hover:border-primary/40"
                               }`}
                               data-testid={`teacher-gap-${d.date}-${s.start_time}`}>
                               {s.start_time}–{s.end_time}
                             </button>
                           );
                         })}
                       </div>
                     </div>
                   ))}
                 </div>
               )}
              {parentReplSlot && (
                <div className="rounded-md bg-primary/10 border border-primary/30 p-2 text-xs flex items-center justify-between"
                  data-testid="parent-selected-slot">
                  <span>
                    Selected: <span className="font-mono font-semibold">
                      {parentReplSlot.date} {parentReplSlot.start_time}–{parentReplSlot.end_time}
                    </span>
                  </span>
                  <button
                    type="button"
                    onClick={() => setParentReplSlot(null)}
                    className="text-muted-foreground hover:text-destructive"
                    data-testid="parent-slot-clear">Clear</button>
                </div>
              )}
              {/* Escape hatch: parent can't find a suitable time */}
              <div className="pt-2 border-t border-primary/20">
                <label className="flex items-start gap-2 text-xs cursor-pointer"
                  data-testid="parent-defer-to-teacher-label">
                  <input type="checkbox"
                    checked={deferToTeacher}
                    onChange={(e) => { setDeferToTeacher(e.target.checked); if (e.target.checked) setParentReplSlot(null); }}
                    className="mt-0.5"
                    data-testid="parent-defer-to-teacher-checkbox"
                  />
                  <div>
                    <p className="font-semibold">Can&apos;t find a suitable time? Ask teacher to propose.</p>
                    <p className="text-muted-foreground text-[10.5px]">
                      Teacher will smart-suggest adjacent (before/after existing lessons within 2 months),
                      open slots, or a Yamaha/public-holiday mirror slot. Leave still auto-approves.
                    </p>
                  </div>
                </label>
              </div>
            </div>
          )}
          {needsEvidence && (
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Evidence {category === "mc" ? "(medical certificate — required)" : "(if applicable)"}
              </Label>
              <Input type="file" ref={fileRef} accept=".jpg,.jpeg,.png,.webp,.pdf,.heic" data-testid="leave-evidence-input" />
            </div>
          )}
          {role === "teacher" && (
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Replacement suggestion (internal — admin only)
              </Label>
              <Textarea rows={2} value={suggestion} onChange={(e) => setSuggestion(e.target.value)}
                placeholder="e.g. I can do replacements next Tue/Thu evening"
                data-testid="leave-suggestion-input" />
            </div>
          )}
          {role === "teacher" && date && lessonDates && (
            <div className="rounded-md border border-dashed border-secondary/60 bg-accent/40 p-3 space-y-3"
              data-testid="proposal-mode-panel">
              <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Structured replacement proposal (optional)
              </Label>
              <div className="flex flex-wrap gap-1.5" data-testid="proposal-mode-chips">
                {[
                  ["none", "None"],
                  ["specific", "Specific student"],
                  ["open_slots", "Open slots"],
                  ["fcfs_open_slots", "FCFS slots"],
                  ["replacement_day", "Replacement day"],
                  ["holiday_mirror", "Holiday mirror"],
                ].map(([v, lbl]) => (
                  <button key={v} type="button" onClick={() => setProposalMode(v)}
                    data-testid={`proposal-mode-${v}`}
                    className={`text-xs px-2.5 py-1 rounded-full border font-mono transition-colors ${
                      proposalMode === v
                        ? "bg-primary text-primary-foreground border-primary"
                        : "bg-card hover:bg-muted/60"}`}>
                    {lbl}
                  </button>
                ))}
              </div>
              {proposalMode === "specific" && (
                <div className="grid grid-cols-2 gap-2" data-testid="proposal-specific-form">
                  <div className="col-span-2 space-y-1">
                    <Label className="text-xs">Affected student</Label>
                    <Select value={specificStudentId} onValueChange={setSpecificStudentId}>
                      <SelectTrigger data-testid="proposal-specific-student"><SelectValue placeholder="Pick student..." /></SelectTrigger>
                      <SelectContent>
                        {(lessonDates.find((d) => d.date === date)?.lessons || []).map((l, i) => (
                          <SelectItem key={i} value={l.class_schedule_id ? (l.student_id || l.class_schedule_id) : String(i)}>
                            {l.student_name || l.course_name} · {l.start_time}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Replacement date</Label>
                    <Input type="date" value={specificDate} onChange={(e) => setSpecificDate(e.target.value)} data-testid="proposal-specific-date" />
                  </div>
                  <div className="grid grid-cols-2 gap-2 col-span-1">
                    <div className="space-y-1">
                      <Label className="text-xs">Start</Label>
                      <Input type="time" value={specificStart} onChange={(e) => setSpecificStart(e.target.value)} data-testid="proposal-specific-start" />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">End</Label>
                      <Input type="time" value={specificEnd} onChange={(e) => setSpecificEnd(e.target.value)} data-testid="proposal-specific-end" />
                    </div>
                  </div>
                </div>
              )}
              {(proposalMode === "open_slots" || proposalMode === "fcfs_open_slots") && (
                <div className="space-y-2" data-testid="proposal-slots-form">
                  <div className="grid grid-cols-4 gap-2 items-end">
                    <div className="space-y-1">
                      <Label className="text-xs">Date</Label>
                      <Input type="date" value={slotDate} onChange={(e) => setSlotDate(e.target.value)} data-testid="proposal-slot-date" />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">From</Label>
                      <Input type="time" value={slotStart} onChange={(e) => setSlotStart(e.target.value)} data-testid="proposal-slot-start" />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">To</Label>
                      <Input type="time" value={slotEnd} onChange={(e) => setSlotEnd(e.target.value)} data-testid="proposal-slot-end" />
                    </div>
                    <Button size="sm" variant="outline" type="button" onClick={() => {
                      if (!slotDate || !slotStart || !slotEnd) { toast.error("Fill date + times"); return; }
                      setOpenSlotsList((p) => [...p, { date: slotDate, start_time: slotStart, end_time: slotEnd }]);
                      setSlotStart(""); setSlotEnd("");
                    }} data-testid="proposal-slot-add">+ Add</Button>
                  </div>
                  {openSlotsList.length > 0 && (
                    <div className="flex flex-wrap gap-1.5" data-testid="proposal-slot-list">
                      {openSlotsList.map((s, i) => (
                        <span key={i} className="text-xs px-2 py-0.5 rounded-full bg-primary text-primary-foreground font-mono">
                          {s.date} {s.start_time}–{s.end_time}
                          <button type="button" className="ml-1 text-secondary"
                            onClick={() => setOpenSlotsList((p) => p.filter((_, j) => j !== i))}>×</button>
                        </span>
                      ))}
                    </div>
                  )}
                  {proposalMode === "fcfs_open_slots" && (
                    <p className="text-[10px] text-muted-foreground">First-come-first-serve: once a slot is claimed it&apos;s gone.</p>
                  )}
                </div>
              )}
              {proposalMode === "replacement_day" && (
                <div className="space-y-1" data-testid="proposal-day-form">
                  <Label className="text-xs">One replacement day — all affected students keep their original time</Label>
                  <Input type="date" value={replacementDay} onChange={(e) => setReplacementDay(e.target.value)} data-testid="proposal-day-date" />
                </div>
              )}
              {proposalMode === "holiday_mirror" && (
                <div className="space-y-1.5" data-testid="proposal-holiday-form">
                  <Label className="text-xs">Yamaha / Public Holiday (same weekday as leave)</Label>
                  {!date ? (
                    <p className="text-[10px] text-muted-foreground">Pick a leave date first.</p>
                  ) : !holidays ? <Spinner /> : (() => {
                    const leaveDow = new Date(date + "T00:00:00").getDay();
                    // Convert JS Sunday=0 to Mon=0..Sun=6 for parity with backend
                    const backendDow = (leaveDow + 6) % 7;
                    const _t = new Date();
                    const today = `${_t.getFullYear()}-${String(_t.getMonth()+1).padStart(2,"0")}-${String(_t.getDate()).padStart(2,"0")}`;
                    const eligible = holidays.filter((h) => {
                      if (h.date <= today) return false;
                      if (h.date === date) return false;
                      const d = new Date(h.date + "T00:00:00");
                      return ((d.getDay() + 6) % 7) === backendDow;
                    }).sort((a, b) => a.date.localeCompare(b.date));
                    if (eligible.length === 0) {
                      return (
                        <p className="text-[10px] text-muted-foreground border border-dashed rounded p-2">
                          No observed Yamaha / Public holiday falls on the same weekday within
                          the school calendar. Ask admin to add one, or pick a different proposal mode.
                        </p>
                      );
                    }
                    return (
                      <>
                        <Select value={holidayDate} onValueChange={setHolidayDate}>
                          <SelectTrigger data-testid="proposal-holiday-date"><SelectValue placeholder="Pick a holiday..." /></SelectTrigger>
                          <SelectContent>
                            {eligible.map((h) => (
                              <SelectItem key={h.date} value={h.date}>
                                {h.date} · {h.holiday_type === "yamaha_school_holiday" ? "Yamaha" : "Public"} · {h.label || h.name || "(no name)"}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <p className="text-[10px] text-muted-foreground">
                          Each affected student will be proposed a replacement at their ORIGINAL time on the chosen holiday
                          (Mirror-Slot rule §19). Parents still confirm individually.
                        </p>
                      </>
                    );
                  })()}
                </div>
              )}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="submit-leave-button">
            {busy ? "Submitting..." : "Submit request"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ------------------------------------------------------------------ teacher / parent list
export function LeaveListPage({ role }) {
  const { data: leaves, loading, reload } = useList("/leave-requests");
  const [open, setOpen] = useState(false);
  const evidenceRefs = useRef({});

  const cancel = async (id) => {
    try {
      await api.post(`/leave-requests/${id}/cancel`);
      toast.success("Leave request cancelled");
      reload();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const attach = async (id, file) => {
    if (!file) return;
    try {
      const ev = await uploadEvidence(file);
      await api.post(`/leave-requests/${id}/evidence`, ev);
      toast.success("Evidence attached — the school will review your request");
      reload();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="leave-list-page">
      <PageHeader title="Leave requests"
        description={role === "parent"
          ? "Standard leave (7 days' notice), MC or Emergency — each uses the student's 7-per-cycle quota."
          : "Request leave / MC / emergency leave — once approved, affected families are notified and replacement requests are created."}
        actions={<Button onClick={() => setOpen(true)} data-testid="new-leave-button">
          <Plane className="h-4 w-4 mr-1" />Request leave</Button>} />
      {loading ? <Spinner full /> : !leaves || leaves.length === 0 ? (
        <EmptyState text="No leave requests yet." />
      ) : (
        <div className="space-y-3 stagger-in">
          {leaves.map((l) => (
            <div key={l.id} className="rounded-lg border bg-card p-5 flex flex-wrap items-center gap-3"
              data-testid={`leave-${l.id}`}>
              <CalendarOff className="h-4 w-4 text-secondary shrink-0" />
              <div className="flex-1 min-w-[220px]">
                <p className="text-sm font-medium flex items-center gap-2 flex-wrap">
                  {l.student_name || l.teacher_name} · <span className="font-mono">{l.leave_date}</span>
                  <span className="text-muted-foreground font-normal">· {leaveTimes(l)}</span>
                  <CategoryBadge category={l.leave_category || "standard"} />
                </p>
                <p className="text-xs mt-1">{l.reason}</p>
                {l.exception_reason && <p className="text-xs mt-1 text-destructive">{l.exception_reason}</p>}
                {l.evidence_url && (
                  <a href={`${process.env.REACT_APP_BACKEND_URL}${l.evidence_url}`} target="_blank" rel="noreferrer"
                    className="text-xs text-secondary inline-flex items-center gap-1 mt-1" data-testid={`evidence-link-${l.id}`}>
                    <Paperclip className="h-3 w-3" />{l.evidence_filename || "View evidence"}
                  </a>
                )}
                {l.admin_note && <p className="text-xs mt-1 text-secondary">School: {l.admin_note}</p>}
              </div>
              <StatusBadge status={l.status} testId={`leave-status-${l.id}`} />
              {l.status === "pending_evidence" && (
                <>
                  <input type="file" className="hidden" accept=".jpg,.jpeg,.png,.webp,.pdf,.heic"
                    ref={(el) => (evidenceRefs.current[l.id] = el)}
                    onChange={(e) => attach(l.id, e.target.files?.[0])} />
                  <Button variant="outline" size="sm" onClick={() => evidenceRefs.current[l.id]?.click()}
                    data-testid={`attach-evidence-${l.id}`}>
                    <Paperclip className="h-4 w-4 mr-1" />Attach evidence
                  </Button>
                </>
              )}
              {["pending", "pending_evidence", "admin_exception", "quota_exceeded"].includes(l.status) && (
                <Button variant="ghost" size="sm" onClick={() => cancel(l.id)} data-testid={`cancel-leave-${l.id}`}>
                  <X className="h-4 w-4 mr-1" />Cancel
                </Button>
              )}
            </div>
          ))}
        </div>
      )}
      <LeaveRequestDialog role={role} open={open} onOpenChange={setOpen} onCreated={reload} />
    </div>
  );
}

// ------------------------------------------------------------------ admin: affected classes preview
function AffectedClassesDialog({ leave, onClose, onDecided }) {
  const [data, setData] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [showReject, setShowReject] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!leave) return;
    setData(null);
    api.get(`/leave-requests/${leave.id}/affected-classes`)
      .then((r) => setData(r.data))
      .catch((e) => toast.error(errMsg(e)));
  }, [leave]);

  if (!leave) return null;
  const canDecide = ["pending", "admin_exception", "quota_exceeded", "pending_evidence"].includes(leave.status);

  const approve = async () => {
    setBusy(true);
    try {
      const { data: res } = await api.post(`/leave-requests/${leave.id}/approve`, { note: null });
      const rr = res.reschedule_requests;
      toast.success(
        `Approved — ${res.affected_classes} affected lesson(s)` +
        (rr ? ` · ${rr.created.length} replacement request(s) created, ${rr.linked.length} linked, ${rr.group_classes.length} group class(es)` : ""),
        { duration: 8000 });
      onDecided(); onClose();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const reject = async () => {
    if (!rejectReason) { toast.error("A rejection reason is required"); return; }
    setBusy(true);
    try {
      await api.post(`/leave-requests/${leave.id}/reject`, { reason: rejectReason });
      toast.success("Leave rejected");
      onDecided(); onClose();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="affected-classes-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2 flex-wrap">
            Affected classes — {leave.teacher_name || leave.student_name} · {leave.leave_date}
            <CategoryBadge category={leave.leave_category || "standard"} />
          </DialogTitle>
          <DialogDescription>
            Review the full impact before deciding. Individual lessons get replacement requests after approval;
            group classes need a class-level arrangement.
          </DialogDescription>
        </DialogHeader>
        {leave.replacement_suggestion && (
          <div className="rounded-md border bg-accent p-3 text-sm" data-testid="teacher-suggestion">
            <span className="font-semibold">Teacher&apos;s replacement suggestion (internal):</span> {leave.replacement_suggestion}
          </div>
        )}
        {data?.proposal_mode && (
          <div className="rounded-md border border-primary/40 bg-accent/50 p-3 text-sm space-y-1"
            data-testid="proposal-preview">
            <p className="font-semibold flex items-center gap-1.5">
              <RefreshCcw className="h-3.5 w-3.5" />
              Structured proposal — {data.proposal_mode.replace(/_/g, " ")}
              {data.proposal_processed ? (
                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-primary text-primary-foreground">processed</span>
              ) : (
                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-muted">pending</span>
              )}
            </p>
            {data.proposal_mode === "specific" && (
              <p className="text-xs">Student {data.proposal_data?.student_id?.slice(0, 8)} · {data.proposal_data?.replacement_date} · {data.proposal_data?.start_time}–{data.proposal_data?.end_time}</p>
            )}
            {(data.proposal_mode === "open_slots" || data.proposal_mode === "fcfs_open_slots") && (
              <div className="text-xs">
                <span className="font-mono">{(data.proposal_data?.slots || []).length} slot(s)</span>
                {(data.proposal_data?.slots || []).slice(0, 6).map((s, i) => (
                  <span key={i} className="ml-1.5 font-mono">· {s.date} {s.start_time}–{s.end_time}</span>
                ))}
              </div>
            )}
            {data.proposal_mode === "replacement_day" && (
              <p className="text-xs">All affected students → replacement day <span className="font-mono">{data.proposal_data?.replacement_day}</span> at their original times</p>
            )}
            {data.proposal_mode === "holiday_mirror" && (
              <p className="text-xs">Holiday <span className="font-mono">{data.proposal_data?.holiday_date}</span> · full engine ships in Phase 5</p>
            )}
            {data.proposal_result && (
              <p className="text-[10px] text-muted-foreground">
                Result: {data.proposal_result.specific_prefilled} specific · {data.proposal_result.slots_opened} opened · {data.proposal_result.slots_rejected} rejected · {data.proposal_result.mirror_day_slots} mirror
              </p>
            )}
          </div>
        )}
        {leave.exception_reason && (
          <div className="rounded-md border border-destructive/40 bg-[#FFEDEC] p-3 text-sm" data-testid="exception-banner">
            {leave.exception_reason}
          </div>
        )}
        {!data ? <Spinner /> : data.classes.length === 0 ? (
          <EmptyState text="No classes are affected on this date." />
        ) : (
          <>
            <p className="text-sm text-muted-foreground">
              {data.total} affected · {data.individual} individual · {data.group} group
            </p>
            <div className="rounded-lg border overflow-x-auto">
              <Table>
                <TableHeader><TableRow className="hover:bg-transparent">
                  <TableHead>Student</TableHead><TableHead>Course</TableHead><TableHead>Time</TableHead>
                  <TableHead>Type</TableHead><TableHead>Parent contact</TableHead>
                  <TableHead>Status</TableHead><TableHead>Existing request</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {data.classes.map((c) => (
                    <TableRow key={c.class_schedule_id} data-testid={`affected-row-${c.class_schedule_id}`}>
                      <TableCell className="text-sm font-medium">{c.student_name}</TableCell>
                      <TableCell className="text-xs">{c.course_name}<br />
                        <span className="text-muted-foreground">{c.studio_name}</span></TableCell>
                      <TableCell className="font-mono text-xs">{c.start_time}–{c.end_time}
                        <br /><span className="text-muted-foreground">{c.duration_minutes} min</span></TableCell>
                      <TableCell>
                        <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full ${c.is_group ? "bg-[#FFF3E0] text-[#B45309]" : "bg-muted"}`}>
                          {c.is_group ? <Users className="h-3 w-3" /> : <User className="h-3 w-3" />}
                          {c.class_type}
                        </span>
                      </TableCell>
                      <TableCell className="text-xs">
                        {(c.parent_contacts || []).map((p, i) => (
                          <p key={i}>{p.name}{p.phone ? ` · ${p.phone}` : ""}</p>
                        ))}
                      </TableCell>
                      <TableCell><StatusBadge status={c.lesson_status} /></TableCell>
                      <TableCell className="text-xs">
                        {c.existing_reschedule_request_id
                          ? <StatusBadge status={c.existing_reschedule_status} />
                          : <span className="text-muted-foreground">—</span>}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </>
        )}
        {showReject && (
          <div className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Rejection reason</Label>
            <Textarea rows={2} value={rejectReason} onChange={(e) => setRejectReason(e.target.value)}
              data-testid="preview-reject-reason" />
          </div>
        )}
        {leave.status === "approved" && leave.leave_type === "teacher" && data && data.group > 0 && (
          <GroupArrangementForm leave={leave} groupClasses={data.classes.filter((c) => c.is_group)}
            onDone={() => { onDecided(); onClose(); }} />
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
          {canDecide && (showReject ? (
            <Button variant="destructive" onClick={reject} disabled={busy} data-testid="preview-confirm-reject">
              Confirm reject
            </Button>
          ) : (
            <>
              <Button variant="outline" className="text-destructive border-destructive/30"
                onClick={() => setShowReject(true)} data-testid="preview-reject-button">
                <X className="h-4 w-4 mr-1" />Reject
              </Button>
              <Button onClick={approve} disabled={busy || !data} data-testid="preview-approve-button">
                <Check className="h-4 w-4 mr-1" />{busy ? "Approving..." : "Approve leave"}
              </Button>
            </>
          ))}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ------------------------------------------------------------------ group class arrangement (class-level)
function GroupArrangementForm({ leave, groupClasses, onDone }) {
  const { data: studios } = useList("/studios");
  const { data: teachers } = useList("/teachers");
  const [type, setType] = useState("replace");
  const [scheduleId, setScheduleId] = useState(groupClasses[0]?.class_schedule_id || "");
  const [date, setDate] = useState("");
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [studioId, setStudioId] = useState("");
  const [subTeacherId, setSubTeacherId] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/leave-requests/${leave.id}/group-arrangement`, {
        class_schedule_id: scheduleId, arrangement_type: type,
        new_date: date || null, new_start_time: startTime || null, new_end_time: endTime || null,
        new_studio_id: studioId || null, substitute_teacher_id: subTeacherId || null,
        note: note || null,
      });
      toast.success(`Class-level arrangement saved — ${data.student_ids.length} student(s) notified`);
      onDone();
    } catch (e) { toast.error(errMsg(e), { duration: 7000 }); } finally { setBusy(false); }
  };

  return (
    <div className="rounded-md border bg-muted/40 p-3 space-y-3" data-testid="group-arrangement-form">
      <p className="text-sm font-semibold flex items-center gap-1.5">
        <Users className="h-4 w-4" />Group class arrangement (applies to the whole class)
      </p>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Group class</Label>
          <Select value={scheduleId} onValueChange={setScheduleId}>
            <SelectTrigger data-testid="group-arr-class-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {groupClasses.map((c) => (
                <SelectItem key={c.class_schedule_id} value={c.class_schedule_id}>
                  {c.course_name} · {c.start_time}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Arrangement</Label>
          <Select value={type} onValueChange={setType}>
            <SelectTrigger data-testid="group-arr-type-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="replace">Replace whole class (new date/time)</SelectItem>
              <SelectItem value="substitute">Assign substitute teacher</SelectItem>
              <SelectItem value="postpone">Postpone / cancel with note</SelectItem>
              <SelectItem value="special">Special arrangement</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      {type === "replace" && (
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">New date</Label>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="group-arr-date" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Studio</Label>
            <Select value={studioId} onValueChange={setStudioId}>
              <SelectTrigger data-testid="group-arr-studio"><SelectValue placeholder="Studio..." /></SelectTrigger>
              <SelectContent>
                {(studios || []).map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Start</Label>
            <Input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} data-testid="group-arr-start" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">End</Label>
            <Input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} data-testid="group-arr-end" />
          </div>
        </div>
      )}
      {type === "substitute" && (
        <div className="space-y-1.5">
          <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Substitute teacher</Label>
          <Select value={subTeacherId} onValueChange={setSubTeacherId}>
            <SelectTrigger data-testid="group-arr-teacher"><SelectValue placeholder="Teacher..." /></SelectTrigger>
            <SelectContent>
              {(teachers || []).filter((t) => t.id !== leave.teacher_id)
                .map((t) => <SelectItem key={t.id} value={t.id}>{t.full_name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      )}
      {(type === "postpone" || type === "special") && (
        <div className="space-y-1.5">
          <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Note to families</Label>
          <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} data-testid="group-arr-note" />
        </div>
      )}
      <Button size="sm" onClick={submit} disabled={busy} data-testid="group-arr-submit">
        {busy ? "Saving..." : "Save class-level arrangement"}
      </Button>
    </div>
  );
}

// ------------------------------------------------------------------ admin: unacknowledged notices queue
function AckQueue() {
  const { data: notices, loading, reload } = useList("/admin/leave-notices?ack=pending");
  if (loading || !notices || notices.length === 0) return null;
  return (
    <div className="rounded-lg border border-destructive/40 bg-[#FFF8F7] p-4 mb-6" data-testid="ack-queue">
      <p className="text-sm font-semibold flex items-center gap-1.5 text-destructive mb-2">
        <BellRing className="h-4 w-4" />
        {notices.length} MC/Emergency notice(s) not yet acknowledged by parents — follow up directly
      </p>
      <div className="space-y-1.5">
        {notices.map((n) => (
          <div key={n.id} className="text-xs flex flex-wrap items-center gap-2" data-testid={`ack-pending-${n.id}`}>
            <span className="font-medium">{n.student_name}</span>
            <span className="font-mono">{n.class_date} {n.start_time}</span>
            <span className="text-muted-foreground">{n.course_name} · Teacher {n.teacher_name}</span>
            <CategoryBadge category={n.leave_category} />
            {n.escalated && <span className="text-destructive font-semibold">ESCALATED</span>}
          </div>
        ))}
      </div>
      <Button variant="ghost" size="sm" className="mt-2" onClick={reload}>Refresh</Button>
    </div>
  );
}

// ------------------------------------------------------------------ P1: urgent MC/EL monitoring
function UrgentMonitoringCard() {
  const { data: rows, loading, reload } = useList("/admin/urgent-monitoring");
  const [followupOpen, setFollowupOpen] = useState(false);
  const [followupLeave, setFollowupLeave] = useState(null);
  const [notices, setNotices] = useState([]);
  const [selectedNotice, setSelectedNotice] = useState("");
  const [method, setMethod] = useState("whatsapp");
  const [result, setResult] = useState("reached");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const openFollowup = async (leave) => {
    setFollowupLeave(leave);
    try {
      const { data } = await api.get("/admin/leave-notices?ack=pending");
      const filtered = (data || []).filter((n) => n.leave_request_id === leave.leave_id);
      setNotices(filtered);
      setSelectedNotice(filtered[0]?.id || "");
    } catch { setNotices([]); }
    setMethod("whatsapp"); setResult("reached"); setNote("");
    setFollowupOpen(true);
  };
  const submitFollowup = async () => {
    if (!selectedNotice) { toast.error("Pick a notice"); return; }
    setBusy(true);
    try {
      await api.post(`/leave-notices/${selectedNotice}/manual-followup`, {
        contact_method: method, result, note });
      toast.success("Follow-up logged");
      setFollowupOpen(false); reload();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const monitorAck = async (leaveId) => {
    try {
      await api.post(`/leave-requests/${leaveId}/monitor-acknowledge`);
      toast.success("Monitoring closed"); reload();
    } catch (e) { toast.error(errMsg(e)); }
  };

  if (loading || !rows) return null;
  const openRows = rows.filter((r) => r.auto_approved && !r.admin_acknowledged);
  if (openRows.length === 0) return null;

  return (
    <>
      <div className="rounded-lg border border-destructive/50 bg-[#FFEDEC] p-4 mb-6" data-testid="urgent-monitoring-card">
        <p className="text-sm font-semibold flex items-center gap-1.5 text-destructive mb-2">
          <BellRing className="h-4 w-4" />
          {openRows.length} urgent MC / Emergency Leave case(s) — auto-approved, monitor acknowledgements
        </p>
        <div className="space-y-2">
          {openRows.map((r) => (
            <div key={r.leave_id} className="rounded-md border bg-card px-3 py-2 text-xs flex flex-wrap items-center gap-2"
              data-testid={`urgent-row-${r.leave_id}`}>
              <span className="font-semibold">{r.teacher_name}</span>
              <CategoryBadge category={r.leave_category} />
              <span className="font-mono">{r.leave_date}</span>
              <span className="text-muted-foreground">
                Notices: {r.notices_total} · ACK {r.acknowledged}/{r.notices_total}
                {r.unacknowledged > 0 && <> · <span className="text-destructive font-semibold">{r.unacknowledged} pending</span></>}
                {r.escalated > 0 && <> · <span className="text-destructive font-bold">{r.escalated} ESCALATED</span></>}
                {r.manual_followups > 0 && <> · {r.manual_followups} manual follow-up(s)</>}
              </span>
              <span className="flex-1" />
              {r.unacknowledged > 0 && (
                <Button size="sm" variant="outline" className="h-7"
                  onClick={() => openFollowup(r)} data-testid={`log-followup-${r.leave_id}`}>
                  Log manual follow-up
                </Button>
              )}
              <Button size="sm" variant="ghost" className="h-7"
                onClick={() => monitorAck(r.leave_id)} data-testid={`monitor-ack-${r.leave_id}`}>
                Close monitoring
              </Button>
            </div>
          ))}
        </div>
      </div>
      <Dialog open={followupOpen} onOpenChange={setFollowupOpen}>
        <DialogContent className="max-w-md" data-testid="followup-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Log manual follow-up</DialogTitle>
            <DialogDescription>
              {followupLeave?.teacher_name} · {followupLeave?.leave_date}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label className="text-xs">Notice / Student</Label>
              <Select value={selectedNotice} onValueChange={setSelectedNotice}>
                <SelectTrigger data-testid="followup-notice-select"><SelectValue placeholder="Select notice..." /></SelectTrigger>
                <SelectContent>
                  {notices.map((n) => (
                    <SelectItem key={n.id} value={n.id}>{n.student_name} · {n.class_date} {n.start_time}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Contact method</Label>
                <Select value={method} onValueChange={setMethod}>
                  <SelectTrigger data-testid="followup-method"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="whatsapp">WhatsApp</SelectItem>
                    <SelectItem value="call">Call</SelectItem>
                    <SelectItem value="sms">SMS</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Result</Label>
                <Select value={result} onValueChange={setResult}>
                  <SelectTrigger data-testid="followup-result"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="reached">Reached</SelectItem>
                    <SelectItem value="not_reached">Not reached</SelectItem>
                    <SelectItem value="left_message">Left message</SelectItem>
                    <SelectItem value="parent_ack_manual">Parent acknowledged manually</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Internal note</Label>
              <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)}
                data-testid="followup-note" placeholder="Anything the next admin should know..." />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFollowupOpen(false)}>Cancel</Button>
            <Button onClick={submitFollowup} disabled={busy || !selectedNotice} data-testid="followup-submit">
              {busy ? "Saving..." : "Log follow-up"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}


// ------------------------------------------------------------------ admin page
const ADMIN_LEAVE_STATUSES = ["pending", "pending_evidence", "admin_exception", "quota_exceeded",
  "approved", "rejected", "cancelled"];

export function AdminLeavePage() {
  const [status, setStatus] = useState("pending");
  const { data: leaves, loading, reload } = useList(
    `/leave-requests${status !== "all" ? `?status=${status}` : ""}`, [status]);
  const [preview, setPreview] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const recreate = async (id) => {
    setBusyId(id);
    try {
      const { data } = await api.post(`/leave-requests/${id}/create-reschedule-requests`);
      toast.success(`${data.created.length} created · ${data.linked.length} already linked · ${data.group_classes.length} group class(es) — no duplicates`);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusyId(null); }
  };

  return (
    <div data-testid="admin-leave-page">
      <PageHeader title="Leave requests"
        description="Approve leave and handle exceptions. Teacher leave: preview affected classes first — replacement arrangement is then agreed directly between teacher and family." />
      <UrgentMonitoringCard />
      <AckQueue />
      <div className="mb-4">
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-48" data-testid="leave-filter-status"><SelectValue /></SelectTrigger>
          <SelectContent>
            {ADMIN_LEAVE_STATUSES.map((s) => (
              <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>
            ))}
            <SelectItem value="all">All</SelectItem>
          </SelectContent>
        </Select>
      </div>
      {loading ? <Spinner full /> : !leaves || leaves.length === 0 ? (
        <EmptyState text={`No ${status === "all" ? "" : status.replace(/_/g, " ") + " "}leave requests.`} />
      ) : (
        <div className="space-y-3 stagger-in">
          {leaves.map((l) => (
            <div key={l.id} className="rounded-lg border bg-card p-5 flex flex-wrap items-center gap-3"
              data-testid={`admin-leave-${l.id}`}>
              <div className="flex-1 min-w-[260px]">
                <p className="text-sm font-medium flex items-center gap-2 flex-wrap">
                  {l.student_name || l.teacher_name}
                  <span className="text-xs px-2 py-0.5 rounded-full bg-muted capitalize">{l.leave_type}</span>
                  <CategoryBadge category={l.leave_category || "standard"} />
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  <span className="font-mono">{l.leave_date}</span> · {leaveTimes(l)} · requested by {l.requested_by_name}
                </p>
                <p className="text-xs mt-1">{l.reason}</p>
                {l.exception_reason && <p className="text-xs mt-1 text-destructive font-semibold">{l.exception_reason}</p>}
                {l.evidence_url ? (
                  <a href={`${process.env.REACT_APP_BACKEND_URL}${l.evidence_url}`} target="_blank" rel="noreferrer"
                    className="text-xs text-secondary inline-flex items-center gap-1 mt-1">
                    <Paperclip className="h-3 w-3" />{l.evidence_filename || "View evidence"}
                  </a>
                ) : (l.leave_category === "mc" &&
                  <p className="text-xs mt-1 text-destructive">No evidence attached</p>)}
                {l.admin_note && <p className="text-xs mt-1 text-secondary">Note: {l.admin_note}</p>}
              </div>
              <StatusBadge status={l.status} testId={`admin-leave-status-${l.id}`} />
              <div className="flex gap-2 flex-wrap">
                <Button size="sm" variant="outline" onClick={() => setPreview(l)}
                  data-testid={`preview-leave-${l.id}`}>
                  <Eye className="h-4 w-4 mr-1" />
                  {["pending", "pending_evidence", "admin_exception", "quota_exceeded"].includes(l.status)
                    ? "Preview & decide" : "View impact"}
                </Button>
                {l.status === "approved" && l.leave_type === "teacher" && (
                  <Button size="sm" variant="outline" onClick={() => recreate(l.id)} disabled={busyId === l.id}
                    data-testid={`recreate-reschedules-${l.id}`}>
                    <RefreshCcw className="h-4 w-4 mr-1" />Sync replacement requests
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      {preview && <AffectedClassesDialog leave={preview} onClose={() => setPreview(null)} onDecided={reload} />}
    </div>
  );
}
